import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Layers, Plus, PackageSearch, CheckCircle2, Clock, Info, XCircle, Eye } from "lucide-react"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDTypography from "components/MDTypography"
import {
  useGetAllOrderRequestsQuery,
  useGetMyOrderRequestsQuery,
  useGetOrdersSharedToMyCenterQuery,
  useGetOrdersForApprovalQuery,
  useSubmitOrderActionMutation,
} from "shared/redux/endpoints/procurementApprovals"
import { useGetPurchaseSuppliersQuery } from "shared/redux/endpoints/procurementPurchases"
import {
  Page,
  Button,
  Checkbox,
} from "../../ui"
import {
  FilterBar,
  DataTable,
  StatusPill,
  UrgencyBadge,
  MoneyText,
  DateText,
  KpiTile,
  KpiStrip,
  ViewToggle,
  ExportButton,
  CardGrid,
  EmptyState,
  ErrorState,
  en,
} from "procurement/design"
import { isCurrentMonth, formatDateTime } from "procurement/design/format/dates"
import useProcurementFilters from "../../hooks/useProcurementFilters"
import useProcurementListFilters from "../../hooks/useProcurementListFilters"
import { ORDER_STATUS, ORDER_STATUS_LABEL } from "../../status"
import { normalizeOrderOrGroupItem } from "../../constants"
import { allowed, ConfirmModal } from "../approvals/DecisionCard"
import { getApiErrorMessage } from "../../orderSubmitFeedback"
import { isAutoRejectedOutcome, AUTO_REJECT_MESSAGE } from "../../actionResultFeedback"
import { showToastSuccess, showToastError } from "utilities/toastUtil"
import OrderQuickDrawer from "./OrderQuickDrawer"
import OrderRowMenu from "./OrderRowMenu"

const STATUS_OPTIONS = Object.entries(ORDER_STATUS_LABEL).map(([value, label]) => ({ value, label }))

const FILTER_DEFAULTS = {
  tab: "",
  search: "",
  status: "",
  category: "",
  region: "",
  center: "",
  department: "",
  item: "",
  supplier: "",
  fromDate: "",
  toDate: "",
  showGrouped: "1",
  density: "comfortable",
  kpi: "",
}

// KPI tiles filter client-side so switching tiles doesn't refetch and zero out
// the other tiles' counts. Each predicate mirrors how its tile is counted.
const KPI_FILTERS = {
  approvedMonth: (o) => o.status === ORDER_STATUS.APPROVED && isCurrentMonth(o.updatedAt || o.createdAt, new Date()),
  awaiting: (o) => o.status === ORDER_STATUS.PENDING,
  info: (o) => o.status === ORDER_STATUS.INFO_REQUESTED,
  rejectedMonth: (o) => o.status === ORDER_STATUS.REJECTED && isCurrentMonth(o.updatedAt || o.createdAt, new Date()),
}

const EXPORT_COLUMNS = [
  { label: en.orders.orCode, value: (r) => r.orCode },
  { label: en.orders.titleCol, value: (r) => r.title },
  { label: en.orders.category, value: (r) => r.categoryName || "" },
  { label: en.orders.requester, value: (r) => r.requester || "" },
  { label: en.orders.total, value: (r) => r.total ?? "" },
  { label: en.orders.status, value: (r) => ORDER_STATUS_LABEL[r.status] || r.status },
  { label: en.orders.urgency, value: (r) => r.urgency || "" },
  { label: en.orders.lastUpdated, value: (r) => formatDateTime(r.lastUpdated) },
]

const toRow = (it) => ({
  key: `${it.type}-${it.id}`,
  id: it.id,
  type: it.type,
  orCode: it.code,
  title: it.title,
  categoryName: it.categoryName,
  region: it.region,
  requester: it.requesterName,
  total: it.total,
  status: it.status,
  urgency: it.urgency,
  lastUpdated: it.updatedAt || it.createdAt,
  createdAt: it.createdAt,
  purchases: it.purchases,
  orders: it.orders,
  allowedActions: it.allowedActions || [],
  overBudget: it.overBudget,
  expandable: it.type === "group",
  isParent: it.type === "group",
})

// allowGroupOrders gates every group-order affordance together: the "Grouped"
// tab, the row-select column that feeds it, and the "Create group order"
// header action. Procurement/PM get it, LXO doesn't. Defaults closed so a
// future caller that forgets to pass it gets the safe behavior rather than
// silently inheriting the capability.
const OrdersWorkspace = ({ role = "requester", defaultTab = "all", allowGroupOrders = false }) => {
  const navigate = useNavigate()
  const isManager = role === "manager"
  const { filters, setFilters, resetFilters } = useProcurementFilters({
    ...FILTER_DEFAULTS,
    tab: defaultTab,
  })
  const tab = filters.tab || defaultTab

  const [selectedIds, setSelectedIds] = useState(() => new Set())
  const [expandedGroups, setExpandedGroups] = useState(() => new Set())
  const [drawer, setDrawer] = useState(null)
  const [bulkAction, setBulkAction] = useState(null)
  const [view, setView] = useState("list")
  const [submitAction] = useSubmitOrderActionMutation()

  const awaitingQ = useGetOrdersForApprovalQuery({ size: 100 }, { skip: !isManager })
  const awaitingCount = awaitingQ.data?.items?.length || 0

  useEffect(() => {
    if (!isManager || defaultTab === "grouped" || defaultTab === "mine") return
    if (filters.tab) return
    if (awaitingCount > 0) setFilters({ tab: "awaiting" })
  }, [isManager, awaitingCount, defaultTab])

  const { categoryOptions, regionOptions, centerOptions, departmentOptions, itemOptions, itemsLoading, queryFilters } =
    useProcurementListFilters(filters, { userCenters: true })
  const { data: suppliers } = useGetPurchaseSuppliersQuery()
  const supplierOptions = useMemo(
    () => (suppliers || []).map((s) => ({ value: String(s.id), label: s.name })),
    [suppliers]
  )

  // `supplier` matches an order/group that has at least one vendor proposal
  // (selected or not) from that supplier — for a group, either at the group
  // level or on any one member order, whichever the proposal was raised on.
  const listParams = {
    ...queryFilters,
    status: filters.status || undefined,
    category: filters.category ? Number(filters.category) : undefined,
    supplier: filters.supplier || undefined,
    size: 100,
  }

  const allQ = useGetAllOrderRequestsQuery(listParams, {
    skip: !isManager || (tab !== "all" && tab !== "grouped"),
  })
  const mineQ = useGetMyOrderRequestsQuery(listParams, {
    skip: (isManager && tab !== "mine") || tab === "shared",
  })
  const approvalQ = useGetOrdersForApprovalQuery(listParams, {
    skip: !isManager || tab !== "awaiting",
  })
  // A2 — orders shared to the viewer's center (both roles can open this tab).
  const sharedQ = useGetOrdersSharedToMyCenterQuery(listParams, {
    skip: tab !== "shared",
  })

  const activeQ =
    tab === "shared"
      ? sharedQ
      : !isManager
      ? mineQ
      : tab === "awaiting"
      ? approvalQ
      : tab === "mine"
      ? mineQ
      : allQ

  const items = useMemo(
    () => (activeQ.data?.items || []).map(normalizeOrderOrGroupItem),
    [activeQ.data]
  )

  const groupByChildId = useMemo(() => {
    const map = new Map()
    items.forEach((it) => {
      if (it.type !== "group") return
      ;(it.orders || []).forEach((o) => map.set(o.id, it))
    })
    return map
  }, [items])

  const filtered = useMemo(() => {
    let list = items.map(toRow)
    if (tab === "grouped") list = list.filter((r) => r.type === "group")
    if (tab === "awaitingMe") list = list.filter((r) => r.status === ORDER_STATUS.INFO_REQUESTED)
    if (tab === "all" && filters.showGrouped === "0") {
      list = list.filter((r) => r.type !== "group" && !groupByChildId.has(r.id))
    }
    const q = filters.search.trim().toLowerCase()
    if (q) {
      list = list.filter((r) => `${r.orCode} ${r.title} ${r.requester || ""}`.toLowerCase().includes(q))
    }
    const kpiFilter = KPI_FILTERS[filters.kpi]
    if (kpiFilter) {
      list = list.filter((r) => kpiFilter({ status: r.status, updatedAt: r.lastUpdated }))
    }
    return list
  }, [items, tab, filters.search, filters.showGrouped, filters.kpi, groupByChildId])

  const tableRows = useMemo(() => {
    const out = []
    filtered.forEach((r) => {
      out.push(r)
      if (r.type === "group" && expandedGroups.has(r.id)) {
        ;(r.orders || []).forEach((o) => {
          out.push({
            ...toRow(normalizeOrderOrGroupItem({ ...o, type: "order" })),
            key: `child-${r.id}-${o.id}`,
            depth: 1,
            parentGroup: r.code,
          })
        })
      }
    })
    return out
  }, [filtered, expandedGroups])

  const kpis = useMemo(() => {
    const source = items.filter((it) => it.type === "order" || !it.orders)
    const orders = items.flatMap((it) =>
      it.type === "group" ? it.orders || [] : [it]
    )
    const approvedMonth = orders.filter(KPI_FILTERS.approvedMonth).length
    const rejectedMonth = orders.filter(KPI_FILTERS.rejectedMonth).length
    const awaiting = isManager ? awaitingCount : orders.filter(KPI_FILTERS.awaiting).length
    const info = orders.filter(KPI_FILTERS.info).length
    return { approvedMonth, awaiting, info, rejectedMonth, source }
  }, [items, isManager, awaitingCount])

  const toggleGroup = (row) =>
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(row.id)) next.delete(row.id)
      else next.add(row.id)
      return next
    })

  const toggleKpi = (key) => setFilters({ kpi: filters.kpi === key ? "" : key })

  const openGrouped = (group) => {
    setFilters({ tab: "grouped" })
    setExpandedGroups(new Set([group.id]))
  }

  // Pending AND Approved orders are selectable, whichever action the
  // checkbox feeds: bulk approve/reject/info_request only ever has something
  // to do on a Pending order — Approved rows just won't have those actions
  // in their `allowedActions`, so `bulkable` below filters them out on its
  // own without needing a stricter selection gate here. POST /group-orders
  // now accepts members in either status (Approved members let a group
  // "leave early" via partial-approve without ever having been fully
  // Pending together), so both are groupable.
  const isSelectableRow = (r) =>
    r.type !== "group" && (r.status === ORDER_STATUS.PENDING || r.status === ORDER_STATUS.APPROVED)
  const selectableRows = tableRows.filter(isSelectableRow)
  const selectedRows = selectableRows.filter((r) => selectedIds.has(r.id))
  const bulkable = selectedRows.filter((r) => allowed(r.allowedActions).length > 0)
  const allSelected = selectableRows.length > 0 && selectableRows.every((r) => selectedIds.has(r.id))

  const columns = useMemo(() => {
    const cols = []
    // Managers select rows for bulk approve/reject; anyone with group-order
    // rights selects them to feed the "Create group order" action.
    if (isManager || allowGroupOrders) {
      cols.push({
        key: "select",
        header: (
          <Checkbox
            checked={allSelected}
            onChange={() =>
              setSelectedIds((prev) =>
                allSelected ? new Set() : new Set(tableRows.filter(isSelectableRow).map((r) => r.id))
              )
            }
          />
        ),
        className: "pc-col-select",
        render: (r) =>
          !isSelectableRow(r) ? null : (
            <Checkbox
              checked={selectedIds.has(r.id)}
              onChange={() =>
                setSelectedIds((prev) => {
                  const next = new Set(prev)
                  if (next.has(r.id)) next.delete(r.id)
                  else next.add(r.id)
                  return next
                })
              }
            />
          ),
      })
    }
    cols.push(
      {
        key: "orCode",
        header: en.orders.orCode,
        strong: true,
        sortable: true,
        render: (r) => (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            {r.type === "group" && <Layers size={14} aria-hidden />}
            {r.orCode}
            {r.type === "group" && (
              // A group row's click is already claimed by the expand
              // chevron (DataTable routes clicks on any expandable row to
              // onToggleExpand, never onRowClick), so quick-viewing a group
              // needs its own explicit affordance here.
              <Button
                variant="ghost"
                size="sm"
                aria-label="Open group"
                onClick={(e) => {
                  e.stopPropagation()
                  openGroupDetail(r)
                }}
              >
                <Eye size={14} aria-hidden />
              </Button>
            )}
          </span>
        ),
      },
      {
        key: "title",
        header: en.orders.titleCol,
        className: "pc-col-title",
        render: (r) => {
          const group = r.type === "order" ? groupByChildId.get(r.id) : null
          return (
            <span title={r.title}>
              <span className="ds-title-2 pc-cell-title">{r.title}</span>
              {group && (
                <button
                  type="button"
                  className="ds-chip"
                  style={{ marginTop: 4 }}
                  onClick={(e) => {
                    e.stopPropagation()
                    openGrouped(group)
                  }}
                >
                  {group.code}
                </button>
              )}
              {r.type === "group" ? (
                <div className="pc-cell-sub">
                  {[
                    en.orders.childOrders((r.orders || []).length),
                    r.categoryName,
                    isManager ? r.region : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              ) : (
                (r.categoryName || (isManager && r.region)) && (
                  <div className="pc-cell-sub">
                    {[r.categoryName, isManager ? r.region : null].filter(Boolean).join(" · ")}
                  </div>
                )
              )}
            </span>
          )
        },
      }
    )
    if (isManager) {
      cols.push({
        key: "requester",
        header: en.orders.requester,
        className: "pc-col-nowrap",
        render: (r) => r.requester || "—",
      })
    }
    cols.push(
      {
        key: "total",
        header: en.orders.total,
        align: "right",
        sortable: true,
        value: (r) => r.total,
        render: (r) => <MoneyText value={r.total} />,
        className: "pc-col-total",
      },
      {
        key: "status",
        header: en.orders.status,
        sortable: true,
        render: (r) => <StatusPill status={r.status} />,
      }
    )
    if (isManager) {
      cols.push({
        key: "urgency",
        header: en.orders.urgency,
        render: (r) => <UrgencyBadge urgency={r.urgency} />,
      })
    }
    cols.push(
      {
        key: "lastUpdated",
        header: en.orders.lastUpdated,
        sortable: true,
        render: (r) => <DateText value={r.lastUpdated} withTime />,
      },
      {
        key: "actions",
        header: en.orders.actions,
        className: "pc-col-actions",
        render: (r) => (
          <span className="pc-row-actions">
            <Button
              variant="secondary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                openOrder(r)
              }}
            >
              Detail
            </Button>
            <OrderRowMenu items={rowMenuItems(r)} />
          </span>
        ),
      }
    )
    return cols
  }, [isManager, allSelected, selectedIds, tableRows, groupByChildId, allowGroupOrders, expandedGroups])

  const tabOptions = (
    isManager
      ? [
          { value: "all", label: en.orders.all },
          { value: "awaiting", label: en.orders.awaitingApproval, count: awaitingCount },
          { value: "mine", label: en.orders.mine },
          { value: "shared", label: "Shared to my center" },
          { value: "grouped", label: en.orders.grouped },
        ]
      : [
          { value: "all", label: en.orders.all },
          { value: "shared", label: "Shared to my center" },
          { value: "grouped", label: en.orders.grouped },
          { value: "awaitingMe", label: en.orders.awaitingMe },
        ]
  ).filter((o) => allowGroupOrders || o.value !== "grouped")

  const showingGrouped = tab === "grouped" && allowGroupOrders
  const createPath = showingGrouped ? "/procurement/order-requests/group/new" : "/procurement/order-requests/new"
  const primaryLabel = showingGrouped ? en.orders.createGroup : en.orders.newOrder

  const runBulk = async (note, deadline) => {
    const results = await Promise.allSettled(
      bulkable.map((r) => submitAction({ id: r.id, action: bulkAction, note, deadline }).unwrap())
    )
    const fulfilled = results.filter((r) => r.status === "fulfilled")
    // Same as the single-order review flow: a fulfilled call whose resulting
    // order landed on Rejected (while the action taken wasn't literally
    // "reject") means the info-request deadline had already passed and the
    // backend auto-rejected it instead — that's not a success.
    const autoRejected = fulfilled.filter((r) =>
      isAutoRejectedOutcome(bulkAction, r.value, ORDER_STATUS.REJECTED)
    ).length
    const ok = fulfilled.length - autoRejected
    const fail = results.length - fulfilled.length
    if (ok) showToastSuccess(ok === 1 ? en.toast.submitted : `${ok} orders updated.`)
    if (autoRejected)
      showToastError(
        autoRejected === 1
          ? AUTO_REJECT_MESSAGE
          : `${autoRejected} order(s) had already passed their info-request deadline and were auto-rejected instead.`
      )
    if (fail) showToastError(getApiErrorMessage(results.find((r) => r.status === "rejected")?.reason, `${fail} could not be updated.`))
    setBulkAction(null)
    setSelectedIds(new Set())
  }

  // No server-side "sort" param to filter by — the FilterBar dropdown that
  // used to drive this was removed since it wasn't backed by an API filter.
  // Columns (total/status/lastUpdated) are still individually sortable by
  // clicking their headers; this just sets the initial order.
  const sortKey = { key: "lastUpdated", dir: "desc" }

  // Secondary actions mirror what the quick-view drawer already links to, so
  // nothing here is a new capability — just reachable straight from the row.
  const rowMenuItems = (r) => {
    if (r.type === "group") {
      return [
        { key: "group", label: "Open group", onClick: () => openGroupDetail(r) },
        {
          key: "toggle",
          label: expandedGroups.has(r.id) ? "Collapse group" : "Expand group",
          onClick: () => toggleGroup(r),
        },
      ]
    }
    return [
      {
        key: "full",
        label: en.common.openFullPage,
        onClick: () => navigate(isManager ? `/procurement/orders/${r.id}` : `/procurement/my-orders/${r.id}`),
      },
      {
        key: "proposals",
        label: "Vendor proposals",
        onClick: () => navigate(`/procurement/order-requests/${r.id}/vendor-proposals`),
      },
    ]
  }

  const openOrder = (r) => {
    if (r.type === "group") {
      openGroupDetail(r)
      return
    }
    const path = isManager ? `/procurement/orders/${r.id}` : `/procurement/my-orders/${r.id}`
    setDrawer({ id: r.id, path, isGroup: false })
  }

  const openGroupDetail = (group) => navigate(`/procurement/orders/groups/${group.id}`)

  const renderOrderCard = (r) => (
    <button type="button" className={`ds-item-card${r.type === "group" ? " is-group" : ""}`} onClick={() => openOrder(r)}>
      <div className="ds-item-card-head">
        <span className="ds-item-card-code">
          {r.type === "group" && <Layers size={13} aria-hidden />}
          {r.orCode}
        </span>
        <StatusPill status={r.status} />
      </div>
      <div>
        <div className="ds-item-card-title" title={r.title}>
          {r.title}
        </div>
        {(r.categoryName || (isManager && r.region)) && (
          <div className="ds-item-card-meta">{[r.categoryName, isManager ? r.region : null].filter(Boolean).join(" · ")}</div>
        )}
        {isManager && r.requester && <div className="ds-item-card-meta">{r.requester}</div>}
      </div>
      <div className="ds-item-card-foot">
        <span style={{ fontWeight: 700, fontSize: 15, color: "var(--text-primary)" }}>
          <MoneyText value={r.total} align="left" />
        </span>
        <div className="ds-item-card-badges">
          <UrgencyBadge urgency={r.urgency} />
        </div>
      </div>
      <div className="ds-item-card-meta">
        {en.orders.lastUpdated}: <DateText value={r.lastUpdated} withTime />
      </div>
    </button>
  )

  return (
    <Page
      className="pc-page--refined pc-page--list"
      breadcrumb={["Procurement", isManager ? "Orders" : "My orders"]}
      title={isManager ? en.orders.title : en.orders.myTitle}
      subtitle={isManager ? en.orders.subtitle : en.orders.mySubtitle}
      actions={
        <>
          {allowGroupOrders && (
            <Button
              variant="secondary"
              leftIcon={<Layers aria-hidden />}
              disabled={!selectedRows.length}
              onClick={() =>
                navigate("/procurement/order-requests/group/new", { state: { orders: selectedRows } })
              }
            >
              {selectedRows.length ? `${en.orders.createGroup} (${selectedRows.length})` : en.orders.createGroup}
            </Button>
          )}
          <Button variant="primary" leftIcon={<Plus aria-hidden />} onClick={() => navigate(createPath)}>
            {primaryLabel}
          </Button>
        </>
      }
    >
      <KpiStrip>
        <KpiTile
          icon={<CheckCircle2 aria-hidden />}
          label={en.orders.approvedThisMonth}
          value={kpis.approvedMonth}
          iconTone="success"
          active={filters.kpi === "approvedMonth"}
          onClick={() => toggleKpi("approvedMonth")}
        />
        <KpiTile
          icon={<Clock aria-hidden />}
          label={en.orders.awaitingApprovalKpi}
          value={kpis.awaiting}
          iconTone="warning"
          active={filters.kpi === "awaiting"}
          onClick={() => toggleKpi("awaiting")}
        />
        <KpiTile
          icon={<Info aria-hidden />}
          label={en.orders.infoRequested}
          value={kpis.info}
          iconTone="info"
          active={filters.kpi === "info"}
          onClick={() => toggleKpi("info")}
        />
        <KpiTile
          icon={<XCircle aria-hidden />}
          label={en.orders.rejectedThisMonth}
          value={kpis.rejectedMonth}
          iconTone="danger"
          active={filters.kpi === "rejectedMonth"}
          onClick={() => toggleKpi("rejectedMonth")}
        />
      </KpiStrip>

      <FilterBar
        title={en.filter.titleOrders}
        values={{ ...filters, tab }}
        onChange={setFilters}
        onReset={resetFilters}
        fields={[
          { key: "search", label: en.filter.search, placeholder: en.filter.searchOrders },
          {
            key: "tab",
            label: en.filter.view,
            type: "select",
            allowEmpty: false,
            defaultValue: defaultTab,
            options: tabOptions.map((o) => ({ value: o.value, label: o.label })),
          },
          { key: "status", label: en.filter.status, type: "select", options: STATUS_OPTIONS, placeholder: en.filter.allStatuses },
          ...(isManager
            ? [{ key: "category", label: en.filter.category, type: "select", options: categoryOptions, placeholder: en.filter.allCategories }]
            : []),
          { key: "region", label: en.filter.region, type: "select", options: regionOptions },
          { key: "center", label: en.filter.center, type: "select", options: centerOptions, placeholder: en.filter.allCenters },
          ...(isManager
            ? [{ key: "department", label: en.filter.department, type: "select", options: departmentOptions, placeholder: en.filter.allDepartments }]
            : []),
          { key: "item", label: en.filter.item, type: "multiselect", options: itemOptions, loading: itemsLoading, placeholder: en.filter.allItems, searchPlaceholder: en.filter.searchItem },
          { key: "supplier", label: en.filter.supplier, type: "select", options: supplierOptions, placeholder: en.filter.allSuppliers },
          { key: "fromDate", label: en.filter.createdFrom, type: "date" },
          { key: "toDate", label: en.filter.createdTo, type: "date" },
        ]}
        extra={
          tab === "all" ? (
            <label className="pc-checkbox" style={{ marginBottom: 0 }}>
              <input
                type="checkbox"
                checked={filters.showGrouped !== "0"}
                onChange={(e) => setFilters({ showGrouped: e.target.checked ? "1" : "0" })}
              />
              <span>{en.orders.showGroupOrders}</span>
            </label>
          ) : null
        }
      />
      {filters.fromDate && filters.toDate && filters.toDate < filters.fromDate && (
        <p className="pc-hint" style={{ margin: "-8px 0 12px", color: "var(--pc-danger)" }}>
          {en.filter.invalidDateRange}
        </p>
      )}

      <div className={`ds-table-toolbar${view === "list" && !activeQ.isError ? " is-attached" : ""}`}>
        <span>{en.common.results(filtered.length)}</span>
        <div className="ds-table-toolbar-right">
          {bulkable.length > 0 && (
            <>
              <Button variant="secondary" size="sm" onClick={() => setBulkAction("approve")}>
                {en.orders.bulkApprove}
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setBulkAction("info_request")}>
                {en.orders.bulkInfo}
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setBulkAction("reject")}>
                {en.orders.bulkReject}
              </Button>
            </>
          )}
          <ViewToggle value={view} onChange={setView} />
          <ExportButton rows={filtered} columns={EXPORT_COLUMNS} filename="orders.csv" />
          <select
            className="pc-control"
            style={{ width: "auto", padding: "6px 28px 6px 10px" }}
            value={filters.density}
            onChange={(e) => setFilters({ density: e.target.value })}
            aria-label={en.common.density}
          >
            <option value="comfortable">{en.common.comfortable}</option>
            <option value="compact">{en.common.compact}</option>
          </select>
        </div>
      </div>

      {activeQ.isError ? (
        <ErrorState
          text={getApiErrorMessage(activeQ.error, en.empty.loadErrorText)}
          action={
            <Button variant="secondary" size="sm" onClick={() => activeQ.refetch()}>
              {en.common.retry}
            </Button>
          }
        />
      ) : view === "grid" ? (
        <CardGrid
          items={filtered}
          renderItem={renderOrderCard}
          empty={
            <EmptyState
              icon={<PackageSearch aria-hidden />}
              title={filters.search || filters.status || filters.kpi ? en.orders.emptyFilteredTitle : en.orders.emptyTitle}
              text={filters.search || filters.status || filters.kpi ? en.empty.noResultsText : en.orders.emptyText}
              action={
                filters.search || filters.status || filters.kpi ? (
                  <Button variant="secondary" size="sm" onClick={resetFilters}>
                    {en.common.clearFilters}
                  </Button>
                ) : (
                  <Button variant="primary" leftIcon={<Plus aria-hidden />} onClick={() => navigate(createPath)}>
                    {primaryLabel}
                  </Button>
                )
              }
            />
          }
        />
      ) : (
        <DataTable
          columns={columns}
          rows={tableRows}
          isLoading={activeQ.isFetching}
          density={filters.density}
          initialSort={sortKey}
          stickyLast
          expandedKeys={expandedGroups}
          onToggleExpand={(r) => (r.type === "group" ? toggleGroup(r) : undefined)}
          onRowClick={(r) => (r.type === "group" ? undefined : openOrder(r))}
          rowClassName={(r) =>
            [
              r.type === "group" ? "ds-row-parent" : r.depth ? "ds-row-child" : null,
              // Expanded group: its members carry the highlight instead.
              r.overBudget && !(r.type === "group" && expandedGroups.has(r.id)) ? "pc-row-over-budget" : null,
            ]
              .filter(Boolean)
              .join(" ") || undefined
          }
          empty={
            <EmptyState
              icon={<PackageSearch aria-hidden />}
              title={filters.search || filters.status || filters.kpi ? en.orders.emptyFilteredTitle : en.orders.emptyTitle}
              text={filters.search || filters.status || filters.kpi ? en.empty.noResultsText : en.orders.emptyText}
              action={
                filters.search || filters.status || filters.kpi ? (
                  <Button variant="secondary" size="sm" onClick={resetFilters}>
                    {en.common.clearFilters}
                  </Button>
                ) : (
                  <Button variant="primary" leftIcon={<Plus aria-hidden />} onClick={() => navigate(createPath)}>
                    {primaryLabel}
                  </Button>
                )
              }
            />
          }
        />
      )}

      <OrderQuickDrawer
        open={!!drawer}
        orderId={drawer && !drawer.isGroup ? drawer.id : null}
        groupId={drawer && drawer.isGroup ? drawer.id : null}
        onClose={() => setDrawer(null)}
        fullPath={drawer?.path}
      />

      {bulkAction && (
        <ConfirmModal
          actionKey={bulkAction}
          order={{
            total: bulkable.reduce((s, r) => s + (Number(r.total) || 0), 0),
            categoryName: `${bulkable.length} selected order${bulkable.length === 1 ? "" : "s"}`,
          }}
          onCancel={() => setBulkAction(null)}
          onSubmit={runBulk}
        />
      )}
    </Page>
  )
}

export default OrdersWorkspace
